import React from "react";
import {
    Box,
    BoxProps,
    Card,
    CircularProgress,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    PaperProps,
    Select,
    SelectChangeEvent,
    TabProps,
    Typography,
    TypographyProps,
    useTheme,
} from "@mui/material";
import * as Styles from "pages/development/settings/Settings.styles";
import ZoneTab from "./tabs/zone.tab";
import SigningTab from "./tabs/signing.tab";
import EmailTab from "./tabs/email.tab";
import DocumentsTab from "./tabs/documents.tab";
import DepartmentsTab from "./tabs/departments.tab";
import LocationsTab from "./tabs/locations/listLocations";
import ResponsibleTab from "./tabs/companies/listResponsible";
import SignersTab from "./tabs/signinUsers/signinUsers";
import ExpirationPeriods from "./tabs/expirationPeriods/expirationPeriods";

import * as tenantService from "services/tenant-service";

import { useTranslation } from "react-i18next";
import { useAuthContext } from "contexts/authContext";
import { ArrowForwardIos } from "@mui/icons-material";
import { guestTenant } from "services/tenant-service";
import { useTenantContext } from "contexts/tenantContext";
import ListCompanies from "./tabs/companies/listCompanies";

export interface IPropsSettings {
    // setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    tenantId: string;
}

interface IProps {
    showMesage: MessageBox;
}

const Settings = (props: IProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [value, setValue] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const { checkAuth } = useAuthContext();
    const { currentTenant } = useTenantContext();
    const [selectedTab, setSelectedTab] = React.useState<string>("zoneInfo");
    const [tabsList] = React.useState<string[]>([
        "zoneInfo",
        "sign",
        "email",
        "documents",
        "departments",
        "locations",
        "companies",
        "responsibles",
        "signers",
        "expirationPeriods",
    ]);
    const [selectedTenantId, setSelectedTenantId] = React.useState<string>("");
    const [tenants, setTenants] = React.useState<guestTenant[]>([]);

    React.useEffect(() => {
        if (currentTenant) {
            tenantService.getNames().then((res: guestTenant[]) => {
                setTenants(res);
                setSelectedTenantId(currentTenant?.id as string);
            });
        }
    }, [currentTenant]);

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const tabProps = (index: number, visible: boolean = true): TabProps => {
        return {
            id: `setting-tab-${index}`,
            "aria-controls": `setting-tabpanel-${index}`,
            // we use display none to hide the disabled tabs and don't interfere with the other tabs.
            // we use this because if we just dont render it the wrong tabs can be displayed
            // there needs to be a better way of doing this
            sx: {
                display: visible ? "inherit" : "none",
            },
        };
    };

    const handleListItemClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        name: string,
    ) => {
        setSelectedTab(name);
    };

    const handleTenantChange = (event: SelectChangeEvent) => {
        setSelectedTenantId(event.target.value as string);
    };

    const settingsWrapperProps: PaperProps = {
        sx: Styles.listRequestsWrapper(theme),
    };

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme),
    };

    const titleProps: TypographyProps = {
        variant: "h4",
        sx: Styles.listRequestsTitle(theme),
    };

    // Please note that we are not using TabContext and TabPanel here
    // because these are at the time of writing not stable and are in the lab packages
    return (
        <Card {...settingsWrapperProps}>
            <Grid container spacing={2} style={{ marginTop: "24xp" }}>
                <Grid
                    item
                    xs={12}
                    md={3}
                    style={{ borderRight: "1px solid #e0e0e0" }}
                >
                    {checkAuth(["TenantAdministrator"]) && (
                        <Box sx={{ padding: "2rem 0 2rem 1rem" }}>
                            <Select
                                size="small"
                                labelId="name"
                                value={selectedTenantId}
                                onChange={handleTenantChange}
                            >
                                {tenants.map((name) => (
                                    <MenuItem key={name.id} value={name.id}>
                                        {name.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}
                    <List>
                        <Divider />
                        {tabsList.map((tab, index) => (
                            <React.Fragment key={index}>
                                <ListItem component="div" disablePadding>
                                    <ListItemButton
                                        selected={selectedTab === tab}
                                        onClick={(event) =>
                                            handleListItemClick(event, tab)
                                        }
                                    >
                                        <ListItemText
                                            primary={t(`settings.${tab}.title`)}
                                            primaryTypographyProps={{
                                                variant: "body2",
                                            }}
                                        />
                                        <ArrowForwardIos fontSize="small" />
                                    </ListItemButton>
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </List>
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={9}
                    style={{ display: "flex", flexDirection: "column" }}
                >
                    {isLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                padding: "1rem",
                            }}
                        >
                            <CircularProgress sx={{ alignSelf: "center" }} />
                        </Box>
                    )}
                    {!isLoading && (
                        <Box style={{ padding: "16px 16px 16px 0" }}>
                            {(() => {
                                switch (selectedTab) {
                                    case "zoneInfo":
                                        return (
                                            <ZoneTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "sign":
                                        return (
                                            <SigningTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "email":
                                        return (
                                            <EmailTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "documents":
                                        return (
                                            <DocumentsTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "departments":
                                        return (
                                            <DepartmentsTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "companies":
                                        return (
                                            <ListCompanies
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "locations":
                                        return (
                                            <LocationsTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "responsibles":
                                        return (
                                            <ResponsibleTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "signers":
                                        return (
                                            <SignersTab
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    case "expirationPeriods":
                                        return (
                                            <ExpirationPeriods
                                                tenantId={selectedTenantId}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })()}
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Card>

        // <Card {...settingsWrapperProps}>
        //     <Box {...titleWrapperProps}>
        //         <Typography {...titleProps}>{t('settings.title')}</Typography>
        //     </Box>
        //     <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        //         <Tabs value={value} onChange={handleChange} variant='standard' centered>
        //             <Tab label={t('settings.zoneInfo.title')} {...tabProps(0)} />
        //             <Tab label={t('settings.sign.title')} {...tabProps(1)} />
        //             <Tab label={t('settings.email.title')} {...tabProps(2)} />
        //             <Tab label={t('settings.documents.title')} {...tabProps(3)} />
        //             <Tab label={t('departments.title')} {...tabProps(4, checkAuth(['Administrator']))} />
        //         </Tabs>
        //     </Box>
        //     {/*
        //         ** NOTE: do not forget to wrap the tabpanel with conditions if its needed for the tab.
        //         ** otherwise you can just set the display to inherit in dev tools and look at the tabpanel
        //     */}
        //     <TabPanel value={value} index={0}>
        //         <ZoneTab setIsLoading={setIsLoading} isLoading={isLoading} />
        //     </TabPanel>
        //     <TabPanel value={value} index={1}>
        //         <SigningTab setIsLoading={setIsLoading} />
        //     </TabPanel>
        //     <TabPanel value={value} index={2}>
        //         <EmailTab setIsLoading={setIsLoading} />
        //     </TabPanel>
        //     <TabPanel value={value} index={3}>
        //         <DocumentsTab setIsLoading={setIsLoading} />
        //     </TabPanel>
        //     {checkAuth(['Administrator']) && (
        //         <TabPanel value={value} index={4}>
        //             <DepartmentsTab setIsLoading={setIsLoading} />
        //         </TabPanel>
        //     )}
        //     {isLoading && (
        //         <Box sx={{
        //             display: 'flex',
        //             justifyContent: 'center',
        //             padding: '1rem'
        //         }}>
        //             <CircularProgress sx={{ alignSelf: 'center' }} />
        //         </Box>
        //     )}
        // </Card>
    );
};

export default Settings;

interface ITabProps {
    value: number;
    index: number;
    children: React.ReactNode;
    [key: string]: any;
}

const TabPanel = (props: ITabProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`setting-tabpanel-${index}`}
            aria-labelledby={`setting-tab-${index}`}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
};
